<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import type { IReservation } from '@/interfaces/IReservation';

  const props = defineProps<{
    reservation: Pick<IReservation, 'status' | 'guestPhone'>;
    editable: boolean;
    canManage: boolean;
    busy: boolean;
  }>();
  const emit = defineEmits<{
    (event: 'edit'): void;
    (event: 'resend'): void;
    (event: 'copyPhone'): void;
  }>();
  const canResend = computed(
    () =>
      props.canManage &&
      ['confirmed', 'arrived', 'seated'].includes(props.reservation.status)
  );
</script>

<template>
  <footer class="reservation-detail-footer">
    <PosUiButton
      v-if="editable"
      color="neutral"
      variant="outline"
      icon="pencil-outline"
      :disabled="busy"
      @click="emit('edit')"
      >{{ $t('reservations.detail.edit') }}</PosUiButton
    >
    <PosUiPopover
      v-if="canResend || reservation.guestPhone"
      align="end"
      placement="top"
    >
      <template #trigger="{ toggle }">
        <PosUiButton
          color="neutral"
          variant="ghost"
          icon="dots-horizontal"
          :aria-label="$t('posV2.moreActions')"
          :disabled="busy"
          @click="toggle"
        />
      </template>
      <template #default="{ close }">
        <div class="more-actions">
          <button
            v-if="canResend"
            type="button"
            :disabled="busy"
            @click="
              close();
              emit('resend');
            "
          >
            {{ $t('reservations.detail.resend') }}
          </button>
          <button
            v-if="reservation.guestPhone"
            type="button"
            :disabled="busy"
            @click="
              close();
              emit('copyPhone');
            "
          >
            {{ $t('reservations.detail.copyPhone') }}
          </button>
        </div>
      </template>
    </PosUiPopover>
  </footer>
</template>

<style scoped lang="scss">
  .reservation-detail-footer {
    @apply tw-flex tw-flex-shrink-0 tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-neutral-200 tw-px-5 tw-py-3;
    .more-actions {
      @apply tw-flex tw-flex-col;
      button {
        @apply tw-min-h-[44px] tw-rounded-lg tw-px-3 tw-text-left tw-text-sm tw-text-neutral-700 hover:tw-bg-neutral-100;
        &:disabled {
          @apply tw-cursor-not-allowed tw-opacity-50;
        }
      }
    }
  }
</style>
